/*
 * Mobile three-state story controller.
 *
 * NOT GSAP. The brief specifies ScrollTrigger with labelsDirectional snap;
 * GSAP + ScrollTrigger is ~70KB gzipped, this project declined GSAP earlier,
 * and the brief's own performance section argues against shipping it to a
 * phone. This does the same job in a few KB: sticky CSS for the pin, one
 * IntersectionObserver + rAF for progress, and a directional snap that waits
 * for scrolling to stop.
 *
 * What it never does: intercept wheel or touch, call preventDefault, install
 * a nested scroller, or apply scroll-snap to the document. The visitor's
 * scrolling is untouched; the only programmatic movement is a smooth
 * scrollTo AFTER they have stopped, which they can override at any time.
 *
 * Progress -> state, matching the brief's timing:
 *   0.00        hero settled (the static hero band above this section)
 *   0.15-0.35   bag rises, navy arrives, meltwater copy in
 *   0.50        meltwater settled
 *   0.60-0.82   crossfade to ice + repeat copy
 *   1.00        repeat settled
 */
(function () {
  'use strict';

  var MQ = '(max-width: 749px)';
  var REDUCED = '(prefers-reduced-motion: reduce)';
  // Settled points sit INSIDE their dwell intervals, not on the boundaries.
  // The old repeat point was 1.0 — the very end of the trigger — so the state
  // was released the instant it arrived and never held long enough to read.
  var SNAP_POINTS = [0, 0.41, 0.88];

  function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }
  function smoothstep(t) { return t * t * (3 - 2 * t); }

  // Ramp a value across [a,b] of the timeline and hold outside it.
  function seg(p, a, b) { return smoothstep(clamp01((p - a) / (b - a))); }

  function Story(root) {
    this.root = root;
    this.sticky = root.querySelector('.mstory__sticky');
    this.bag = root.querySelector('[data-mstory-bag]');
    this.ice = root.querySelector('[data-mstory-ice]');
    this.navy = root.querySelector('[data-mstory-navy]');
    this.copies = [].slice.call(root.querySelectorAll('[data-mstory-copy]'));
    this.cubes = [].slice.call(root.querySelectorAll('[data-mstory-cube]'));
    // The off-track colour finale is a SIBLING of the story, not a child, so
    // it is reached from the document rather than from root.
    this.finale = document.querySelector('.sstory__finale');
    this.finaleRow = this.finale && this.finale.querySelector('[data-finale-row]');
    this.finaleSlots = this.finaleRow ? [].slice.call(this.finaleRow.children) : [];
    this.cubeUrls = this.finaleRow
      ? (this.finaleRow.dataset.cubeUrls || '').split(',').filter(Boolean) : [];
    // The first three are already on the slots; the cycle picks up after them.
    this.finaleIdx = this.finaleSlots.length;
    this.finaleOpen = false;
    this.finaleTimer = null;
    if (!this.sticky || !this.bag || !this.ice || this.copies.length < 2) return;

    this.mq = window.matchMedia(MQ);
    this.reduced = window.matchMedia(REDUCED);
    this.progress = 0;
    this.visible = false;
    this.ticking = false;
    this.snapTimer = null;
    this.lastY = window.scrollY;
    this.dir = 1;
    this.settled = 0;

    this.onScroll = this.handleScroll.bind(this);
    this.onResize = this.handleResize.bind(this);
    this.onMode = this.evaluate.bind(this);
    this.tick = this.update.bind(this);

    this.addMQ(this.mq, this.onMode);
    this.addMQ(this.reduced, this.onMode);
    this.evaluate();
  }

  Story.prototype.addMQ = function (mq, fn) {
    if (mq.addEventListener) mq.addEventListener('change', fn);
    else mq.addListener(fn);
  };

  Story.prototype.removeMQ = function (mq, fn) {
    if (mq.removeEventListener) mq.removeEventListener('change', fn);
    else mq.removeListener(fn);
  };

  Story.prototype.evaluate = function () {
    var want = this.mq.matches && !this.reduced.matches;
    if (want === this.mounted) return;
    if (want) this.mount(); else this.unmount();
  };

  Story.prototype.mount = function () {
    this.mounted = true;
    // Revealing here, not in Liquid, is what makes the static bands the
    // fallback for no-JS and reduced motion.
    this.root.hidden = false;
    this.root.classList.add('mstory--on');

    var self = this;
    this.io = new IntersectionObserver(function (entries) {
      self.visible = entries[0].isIntersecting;
      if (self.visible) { self.measure(); self.requestUpdate(); }
    }, { rootMargin: '10% 0px' });
    this.io.observe(this.root);

    /*
     * The colour row drifts into place as you reach it, so the heap that
     * gathered at the bag's foot reads as having travelled down into the
     * lineup rather than the lineup simply being there already.
     *
     * The pre-state is applied by JS, never in the stylesheet: no-JS and
     * reduced motion must find the row already in position, not offset and
     * waiting for a script that is not coming.
     */
    if (this.finale) {
      this.finale.classList.add('is-anim');
      this.finaleIO = new IntersectionObserver(function (entries) {
        var on = entries[0].isIntersecting;
        // The drift is one-shot; the conveyor is not, so this observer keeps
        // running and simply gates the interval on the row being on screen.
        if (on) entries[0].target.classList.add('is-in');
        self.finaleInView = on;
        self.setFinaleCycle(on);
      }, { rootMargin: '0px 0px -18% 0px' });
      this.finaleIO.observe(this.finale);
    }

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
    window.addEventListener('orientationchange', this.onResize, { passive: true });
    // Measure AFTER the browser has laid out with .mstory--on applied. That
    // class hides the two static bands, which shortens the document above
    // this section — measuring first caches a top that is hundreds of pixels
    // too low and pins progress at 0 through the middle of the sequence.
    var m = this.measure.bind(this);
    requestAnimationFrame(function () { m(); self.update(); });
    this.measure();
    this.update();
  };

  /*
   * The colour conveyor, phone-sized: two cubes on show, one waiting off the
   * right. Each turn drops the waiting cube in, walks the row one place left
   * and pushes the leftmost off the end. Push, pop — the same move the
   * desktop dock makes, with two slots instead of four.
   *
   * Colour is a `src` swap, done only while a slot is parked off-stage at
   * zero opacity, so nothing is ever cross-faded and the cutouts' outlines
   * never have to agree with each other. One colour is fetched ahead of the
   * one being shown, which is a whole tick of lead time and means a phone
   * only ever pays for the colours it actually reaches.
   */
  var FINALE_MS = 1500;

  Story.prototype.setFinaleCycle = function (on) {
    if (on && !this.finaleTimer && this.cubeUrls.length > 1 && this.finaleSlots.length > 2) {
      var self = this;
      this.finaleTimer = setInterval(function () { self.finaleTick(); }, FINALE_MS);
    } else if (!on && this.finaleTimer) {
      clearInterval(this.finaleTimer);
      this.finaleTimer = null;
    }
  };

  // Land the move already on screen. Every slot is one place left of where
  // the flex row would put it, so re-homing the leftmost and clearing every
  // offset together is an identity: nothing visible moves except the spent
  // slot, from off the left to off the right, at zero opacity.
  Story.prototype.finaleCommit = function () {
    if (!this.finaleOpen || !this.finaleRow) return;
    this.finaleOpen = false;
    var slots = [].slice.call(this.finaleRow.children);
    for (var i = 0; i < slots.length; i++) {
      slots[i].classList.remove('is-sliding'); // drop the transition FIRST,
      slots[i].style.removeProperty('--slide'); // so neither of these eases
      slots[i].style.removeProperty('--drop');
    }
    slots[0].style.opacity = '0';
    this.finaleRow.appendChild(slots[0]);
  };

  Story.prototype.finaleTick = function () {
    if (!this.finaleRow || document.hidden) return;
    this.finaleCommit();
    var slots = [].slice.call(this.finaleRow.children);
    if (slots.length < 3) return;
    var onDeck = slots[slots.length - 1];
    var leaving = slots[0];
    // Measured off the laid-out row: the slot width is a min() of a rem and
    // a vw, so the gap between two of them is the only honest pitch.
    var step = slots[1].offsetLeft - slots[0].offsetLeft;
    if (!step) return;

    var skin = onDeck.querySelector('[data-finale-skin]');
    if (skin) skin.src = this.cubeUrls[this.finaleIdx % this.cubeUrls.length];
    this.finaleIdx = (this.finaleIdx + 1) % this.cubeUrls.length;
    // Fetch the next one now so it is decoded by the time its turn comes.
    var ahead = new Image();
    ahead.src = this.cubeUrls[this.finaleIdx % this.cubeUrls.length];

    // Park the incoming cube high and invisible, and make the browser treat
    // that as a finished state before the transition is allowed to start.
    onDeck.classList.remove('is-sliding');
    onDeck.style.setProperty('--drop', (-step * 0.42).toFixed(1) + 'px');
    onDeck.style.opacity = '0';
    void onDeck.offsetWidth;

    for (var j = 0; j < slots.length; j++) {
      slots[j].classList.add('is-sliding');
      slots[j].style.setProperty('--slide', (-step).toFixed(1) + 'px');
    }
    onDeck.style.setProperty('--drop', '0px');
    onDeck.style.opacity = '1';
    // Further than the rest, so it reads as pushed off rather than stopping
    // one slot short.
    leaving.style.setProperty('--slide', (-step * 1.5).toFixed(1) + 'px');
    leaving.style.opacity = '0';
    this.finaleOpen = true;
  };

  Story.prototype.unmount = function () {
    this.setFinaleCycle(false);
    if (this.finaleIO) { this.finaleIO.disconnect(); this.finaleIO = null; }
    if (this.finale) this.finale.classList.remove('is-anim', 'is-in');
    this.finaleSlots.forEach(function (s2) {
      s2.classList.remove('is-sliding');
      s2.style.removeProperty('--slide');
      s2.style.removeProperty('--drop');
      s2.style.removeProperty('opacity');
    });
    this.finaleOpen = false;
    this.mounted = false;
    this.root.classList.remove('mstory--on');
    this.root.hidden = true;
    if (this.io) { this.io.disconnect(); this.io = null; }
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('orientationchange', this.onResize);
    clearTimeout(this.snapTimer);
    // Leave nothing transformed behind.
    this.bag.style.transform = '';
    this.ice.style.opacity = '';
    if (this.navy) this.navy.style.opacity = '';
    this.cubes.forEach(function (c) { c.style.translate = ''; });
    this.copies.forEach(function (c) { c.style.opacity = ''; c.style.transform = ''; });
  };

  Story.prototype.measure = function () {
    var r = this.root.getBoundingClientRect();
    this.top = r.top + window.scrollY;
    // Travel is the section height less the one sticky screen that stays put.
    this.travel = Math.max(1, this.root.offsetHeight - this.sticky.offsetHeight);
    this.stageH = this.sticky.offsetHeight;
  };

  Story.prototype.handleResize = function () {
    // Safari changing toolbar height fires resize; re-measure rather than
    // caching a viewport figure that silently goes stale.
    this.measure();
    this.requestUpdate();
  };

  Story.prototype.handleScroll = function () {
    var y = window.scrollY;
    if (y !== this.lastY) { this.dir = y > this.lastY ? 1 : -1; this.lastY = y; }
    this.requestUpdate();
    this.queueSnap();
  };

  Story.prototype.requestUpdate = function () {
    if (this.ticking || !this.mounted) return;
    this.ticking = true;
    requestAnimationFrame(this.tick);
  };

  Story.prototype.update = function () {
    this.ticking = false;
    if (!this.mounted) return;

    var p = clamp01((window.scrollY - this.top) / this.travel);
    this.progress = p;

    if (!this.visible) return;

    // ---- bag: the only real movement, and only in the first transition ----
    var rise = seg(p, 0.08, 0.32);
    var settle = seg(p, 0.5, 0.76) * 8; // a token 8px, nothing more
    // The rise moves the bag OUT of its layout box while the copy row stays
    // where it is, which opened ~150px of dead space between them. The copy
    // travels with it, so the pair reads as one composition and the text
    // stays directly under the bag.
    // At rest the bag hangs lower than it needs to, because the stage
    // distributes its rows for the SETTLED states where copy is present. A
    // lift that exists only at p=0 and eases out as the story starts buys
    // height for the first screen without taking it from the copy's padding.
    //
    // Clamped to the headroom the stage actually has: the stage clips
    // overflow, so on a short screen (iPhone XS) a fixed 46px shoved the
    // bag's top through the boundary and it rendered decapitated, flat
    // against the cyan. offsetTop ignores transforms, so it is the
    // untransformed gap.
    var headroom = Math.max(0, this.bag.offsetTop - 6);
    var restLift = -Math.min(46, headroom) * (1 - rise);
    // The settled rise scales with the stage rather than being a flat 110px.
    // On an iPhone XS with chrome expanded, 110px of a 635px stage rammed the
    // bag against the top edge (1%) and left a third of the screen empty
    // below the copy; 12% of the stage keeps the same look on tall screens
    // and stays centred on short ones.
    var riseMax = Math.min(44, (this.stageH || 700) * 0.05);
    var groupRise = -riseMax * rise + restLift;
    this.groupRise = groupRise;
    var y = groupRise - settle;
    var scale = 1 - 0.22 * rise;
    this.bag.style.transform = 'translate3d(0,' + y.toFixed(1) + 'px,0) scale(' + scale.toFixed(3) + ')';

    // ---- cubes: drift up as the story advances, each at its own rate, so
    // scrolling down reads as descending through them. Uses the independent
    // `translate` property so the CSS rotation is not clobbered. ----
    for (var ci = 0; ci < this.cubes.length; ci++) {
      var cube = this.cubes[ci];
      var depth = parseFloat(cube.dataset.depth) || 1;
      this.cubes[ci].style.translate = '0 ' + ((0.5 - p) * 26 * depth).toFixed(2) + 'vh';
    }

    // ---- background: cyan base, navy over it, then ice over that ----
    if (this.navy) this.navy.style.opacity = seg(p, 0.06, 0.30).toFixed(3);
    this.ice.style.opacity = seg(p, 0.52, 0.74).toFixed(3);

    // ---- copy: never both readable at once ----
    var inA = seg(p, 0.12, 0.30);
    var outA = seg(p, 0.52, 0.66);
    var a = inA * (1 - outA);
    var b = seg(p, 0.60, 0.76);
    var g = this.groupRise;
    this.setCopy(this.copies[0], a, g + (1 - inA) * 12 - outA * 10);
    this.setCopy(this.copies[1], b, g + (1 - b) * 12);
  };

  Story.prototype.setCopy = function (el, opacity, shift) {
    el.style.opacity = opacity.toFixed(3);
    el.style.transform = 'translate3d(0,' + shift.toFixed(1) + 'px,0)';
    // A faded-out block must not swallow taps meant for what is behind it.
    el.style.pointerEvents = opacity > 0.9 ? 'auto' : 'none';
  };

  /*
   * Directional snap. Fires only after scrolling has stopped, so it never
   * fights the finger, and only forward-of-direction so a gesture is never
   * undone. Outside the section it does nothing at all.
   */
  Story.prototype.queueSnap = function () {
    if (!this.mounted || !this.visible) return;
    var self = this;
    clearTimeout(this.snapTimer);
    this.snapTimer = setTimeout(function () { self.snap(); }, 90);
  };

  Story.prototype.snap = function () {
    var p = this.progress;
    // Only stand down once genuinely clear of the sequence. The old guard
    // bailed at p >= 0.999, which is exactly where an iOS flick lands — so
    // the one case that needed catching was the one case skipped.
    if (p <= 0.0005 || p >= 0.9995) return;

    var target = null;
    for (var i = 0; i < SNAP_POINTS.length; i++) {
      var s = SNAP_POINTS[i];
      if (this.dir > 0 && s > p + 0.02) { target = s; break; }
      if (this.dir < 0 && s < p - 0.02) target = s; // keep the nearest below
    }
    if (target === null) return;
    // Do not drag someone back who is clearly leaving the section.
    // Never cross more than one state in a single settle, however far the
    // momentum carried. This is what stops a flick landing in the product
    // section with both benefits unseen.
    var idx = SNAP_POINTS.indexOf(target);
    var from = 0;
    for (var j = 0; j < SNAP_POINTS.length; j++) {
      if (Math.abs(SNAP_POINTS[j] - this.settled) < Math.abs(SNAP_POINTS[from] - this.settled)) from = j;
    }
    if (Math.abs(idx - from) > 1) target = SNAP_POINTS[from + (idx > from ? 1 : -1)];
    this.settled = target;

    window.scrollTo({ top: Math.round(this.top + target * this.travel), behavior: 'smooth' });
  };

  Story.prototype.destroy = function () {
    this.unmount();
    this.removeMQ(this.mq, this.onMode);
    this.removeMQ(this.reduced, this.onMode);
  };

  var instances = [];

  function init(scope) {
    (scope || document).querySelectorAll('[data-mstory]').forEach(function (el) {
      if (el.dataset.mstoryReady === '1') return;
      el.dataset.mstoryReady = '1';
      instances.push(new Story(el));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme editor: tear the old one down before a reloaded section builds a
  // second timeline over the top of it.
  document.addEventListener('shopify:section:unload', function () {
    instances = instances.filter(function (s) {
      if (document.body.contains(s.root)) return true;
      s.destroy();
      return false;
    });
  });
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
